import { type TRPCQueryKey, useSubscription } from '@trpc/tanstack-react-query'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { StructuredLogTree } from '@repo/sdk'

import { useStatusTelemetry } from '@/hooks/use-status-telemetry'
import { useAuth } from '@/lib/auth'
import { canHoldStream } from '@/lib/realtime-transport'
import type { WaitingDelivery } from '@/lib/waiting-state'
import { advanceAppliedRevision, shouldApplyRevision } from '@/lib/game-stream'
import { isTerminalGameStatus, playRefetchInterval } from '@/lib/play-polling'
import type {
  CompactFallbackReason,
  PlayStatusTransport,
} from '@/lib/play-status-source'
import {
  adaptCompactSnapshot,
  classifyCompactFallback,
  mergeCompactLog,
  isUsableBootstrap,
  isUsableCompactSnapshot,
  resolveCompactStatusEnabled,
} from '@/lib/play-status-source'
import { perspectiveTrpcClient, trpcClient, useTRPC } from '@/lib/trpc'
import type { GameStatusData } from '@/lib/types'

interface WithGameStatus {
  game?: {
    status?: string
    tournamentEditionId?: string | null
    tournamentState?: string | null
  }
}

function isRecoveringTournament(snapshot: WithGameStatus | undefined) {
  return !!snapshot?.game?.tournamentEditionId
    && snapshot.game.tournamentState === 'IN_PROGRESS'
}

function pollingSnapshot(data: unknown) {
  const snapshot = data as WithGameStatus | undefined
  if (snapshot?.game?.status === 'error' && isRecoveringTournament(snapshot)) {
    return { ...snapshot, game: { ...snapshot.game, status: 'paused' } }
  }
  return data
}

export interface UsePlayStatusResult {
  data: GameStatusData | undefined
  dataUpdatedAt: number
  fallbackReason: CompactFallbackReason | null
  isError: boolean
  isLoading: boolean
  markApplied: (statusVersion: string | null) => void
  refetch: () => Promise<unknown>
  transport: PlayStatusTransport
  delivery: WaitingDelivery
}

interface UsePlayStatusOptions {
  perspectiveToken?: string | null
  perspectiveFingerprint?: string | null
}

function perspectiveQueryKey(
  procedureKey: TRPCQueryKey,
  gameId: string | undefined,
  perspectiveFingerprint: string | null,
): TRPCQueryKey {
  return [
    procedureKey[0],
    {
      input: { gameId, perspectiveFingerprint },
      type: 'query',
    },
  ]
}

export function usePlayStatus(
  gameId: string | undefined,
  options: UsePlayStatusOptions = {},
): UsePlayStatusResult {
  const trpc = useTRPC()
  const telemetry = useStatusTelemetry(gameId)
  const perspectiveToken = options.perspectiveToken ?? null
  const perspectiveFingerprint = options.perspectiveFingerprint ?? null
  const sharedPerspective = perspectiveToken !== null
  const perspectiveReady
    = !sharedPerspective || perspectiveFingerprint !== null
  const { actor, authResolved } = useAuth()
  const requestIdentity = sharedPerspective
    ? `shared:${perspectiveFingerprint ?? 'pending'}`
    : `session:${actor?.id ?? 'visitor'}`
  const statusIdentity = `${gameId ?? 'none'}:${requestIdentity}`
  const [streamRefused, setStreamRefused] = useState(false)
  const [streamHealthy, setStreamHealthy] = useState(false)
  const [everConnected, setEverConnected] = useState(false)
  const [appliedRevision, setAppliedRevision] = useState<number | null>(null)
  useEffect(() => {
    setStreamRefused(false)
    setStreamHealthy(false)
    setEverConnected(false)
    setAppliedRevision(null)
  }, [gameId, requestIdentity])

  const [fallbackReason, setFallbackReason]
    = useState<CompactFallbackReason | null>(() =>
      resolveCompactStatusEnabled() ? null : 'disabled',
    )
  useEffect(() => {
    setFallbackReason(resolveCompactStatusEnabled() ? null : 'disabled')
  }, [gameId, requestIdentity])
  const compactEnabled = fallbackReason === null
  const [logState, setLogState] = useState<{
    log: StructuredLogTree | null
    revision: number
    identity: string
  }>({ log: null, revision: 0, identity: statusIdentity })
  const log = logState.identity === statusIdentity ? logState.log : null
  useEffect(() => {
    setLogState({ log: null, revision: 0, identity: statusIdentity })
  }, [statusIdentity])

  const fallBack = useCallback((reason: CompactFallbackReason) => {
    setFallbackReason(previous => previous ?? reason)
  }, [])

  const legacyOptions = trpc.playRouter.status.queryOptions({
    gameId: gameId!,
  })
  const legacyRequest = sharedPerspective
    ? async () =>
      perspectiveTrpcClient.playRouter.status.query({
        gameId: gameId!,
        perspectiveToken: perspectiveToken!,
      })
    : async () => trpcClient.playRouter.status.query({ gameId: gameId! })
  const legacyQueryFn = sharedPerspective
    ? legacyRequest
    : telemetry.instrument(legacyRequest)
  const legacyQuery = useQuery({
    ...legacyOptions,
    enabled:
      !!gameId
      && perspectiveReady
      && (sharedPerspective || authResolved)
      && !compactEnabled,
    queryKey: perspectiveQueryKey(
      legacyOptions.queryKey,
      gameId,
      requestIdentity,
    ),
    queryFn: legacyQueryFn,
    refetchInterval: query => playRefetchInterval({
      state: { ...query.state, data: pollingSnapshot(query.state.data) },
    }, streamHealthy),
    refetchIntervalInBackground: true,
  })

  const bootstrapOptions = trpc.playRouter.bootstrap.queryOptions({
    gameId: gameId!,
  })
  const bootstrapQueryFn = sharedPerspective
    ? () =>
        perspectiveTrpcClient.playRouter.bootstrap.query({
          gameId: gameId!,
          perspectiveToken: perspectiveToken!,
        })
    : () => trpcClient.playRouter.bootstrap.query({ gameId: gameId! })
  const bootstrapQuery = useQuery({
    ...bootstrapOptions,
    enabled:
      !!gameId
      && perspectiveReady
      && (sharedPerspective || authResolved)
      && compactEnabled,
    queryKey: perspectiveQueryKey(
      bootstrapOptions.queryKey,
      gameId,
      requestIdentity,
    ),
    queryFn: bootstrapQueryFn,
    refetchInterval: false,
    staleTime: Number.POSITIVE_INFINITY,
  })

  const compactQuery = useQuery({
    enabled:
      !!gameId
      && perspectiveReady
      && (sharedPerspective || authResolved)
      && compactEnabled
      && bootstrapQuery.isSuccess,
    queryKey: sharedPerspective
      ? [
          'playRouter.compactStatus',
          gameId,
          'perspective',
          perspectiveFingerprint,
        ]
      : ['playRouter.compactStatus', gameId, requestIdentity],
    queryFn: sharedPerspective
      ? () =>
          perspectiveTrpcClient.playRouter.compactStatus.query({
            gameId: gameId!,
            perspectiveToken: perspectiveToken!,
            logCursor: logState.identity === statusIdentity ? logState.revision : 0,
          })
      : telemetry.instrument(async () =>
          trpcClient.playRouter.compactStatus.query({
            gameId: gameId!,
            logCursor: logState.identity === statusIdentity ? logState.revision : 0,
          }),
        ),
    refetchInterval: query => playRefetchInterval({
      state: { ...query.state, data: pollingSnapshot(query.state.data) },
    }, streamHealthy),
    refetchIntervalInBackground: true,
  })

  const snapshot = compactQuery.data
  const bootstrap = bootstrapQuery.data
  useEffect(() => {
    if (compactEnabled
      && snapshot?.game.tournamentEditionId
      && (snapshot.game.tournamentState !== 'IN_PROGRESS'
        || !snapshot.game.tournamentCompetitionOpen)) {
      fallBack('recorded_tournament')
    }
  }, [compactEnabled, fallBack, snapshot])

  useEffect(() => {
    if (!compactEnabled || !isUsableCompactSnapshot(snapshot))
      return
    setLogState(previous => ({
      log: mergeCompactLog(
        previous.identity === statusIdentity ? previous.log : null,
        snapshot,
      ),
      identity: statusIdentity,
      revision: snapshot.logDelta?.revision ?? previous.revision,
    }))
  }, [compactEnabled, snapshot, statusIdentity])

  useEffect(() => {
    if (!compactEnabled)
      return
    const settled
      = (bootstrap !== undefined || bootstrapQuery.error)
        && (snapshot !== undefined || compactQuery.error)
    if (!settled)
      return
    const reason = classifyCompactFallback({
      bootstrap,
      bootstrapError: bootstrapQuery.error,
      enabled: true,
      snapshot,
      snapshotError: compactQuery.error,
    })
    if (reason)
      fallBack(reason)
  }, [
    bootstrap,
    bootstrapQuery.error,
    compactEnabled,
    compactQuery.error,
    fallBack,
    snapshot,
  ])

  const lastSnapshot = (compactEnabled ? snapshot : legacyQuery.data) as
    | WithGameStatus
    | undefined
  const terminal = isTerminalGameStatus(lastSnapshot?.game?.status ?? null)
    && !isRecoveringTournament(lastSnapshot)

  const streaming
    = !sharedPerspective && canHoldStream(actor, authResolved) && !streamRefused

  const refetchStatus = useCallback(() => {
    void (compactEnabled ? compactQuery.refetch() : legacyQuery.refetch())
  }, [compactEnabled, compactQuery, legacyQuery])

  const onGameFrame = useCallback(
    (frame: { type: string, revision?: number }) => {
      const revision
        = frame.type === 'game.changed' ? (frame.revision ?? null) : null
      if (!shouldApplyRevision(appliedRevision, revision))
        return
      setAppliedRevision(previous =>
        advanceAppliedRevision(previous, revision),
      )
      refetchStatus()
    },
    [appliedRevision, refetchStatus],
  )

  const onGameConnectionState = useCallback(
    (state: { state: string }) => {
      const connected = state.state === 'pending'
      setStreamHealthy(connected)
      if (!connected)
        return
      setEverConnected(true)
      refetchStatus()
    },
    [refetchStatus],
  )

  useSubscription(
    trpc.playRouter.onGameChanged.subscriptionOptions(
      { gameId: gameId ?? '' },
      {
        enabled: streaming && !!gameId && !terminal,
        onData: onGameFrame,
        onConnectionStateChange: onGameConnectionState,
        onError: () => {
          setStreamRefused(true)
          setStreamHealthy(false)
        },
      },
    ),
  )

  const adapted = useMemo(() => {
    if (
      !compactEnabled
      || log === null
      || !isUsableBootstrap(bootstrap)
      || !isUsableCompactSnapshot(snapshot)
    ) {
      return
    }
    return adaptCompactSnapshot({ bootstrap, log, snapshot })
  }, [bootstrap, compactEnabled, log, snapshot])

  const delivery: WaitingDelivery = (() => {
    if (sharedPerspective)
      return 'polling'
    if (streamHealthy)
      return 'streaming'
    if (terminal || !canHoldStream(actor, authResolved))
      return 'polling'
    if (streamRefused)
      return 'fallback'
    return everConnected ? 'reconnecting' : 'polling'
  })()

  if (compactEnabled) {
    const permissionUnavailable = (compactQuery.isError || bootstrapQuery.isError)
      && !!adapted?.game.tournamentEditionId
    return {
      data: permissionUnavailable
        ? undefined
        : adapted as unknown as GameStatusData | undefined,
      dataUpdatedAt: compactQuery.dataUpdatedAt,
      fallbackReason,
      isError: compactQuery.isError || bootstrapQuery.isError,
      isLoading: compactQuery.isLoading || bootstrapQuery.isLoading,
      markApplied: telemetry.markApplied,
      refetch: () => compactQuery.refetch(),
      transport: 'compact',
      delivery,
    }
  }

  return {
    data: legacyQuery.isError && legacyQuery.data?.game.tournamentEditionId
      ? undefined
      : legacyQuery.data,
    dataUpdatedAt: legacyQuery.dataUpdatedAt,
    fallbackReason,
    isError: legacyQuery.isError,
    isLoading: legacyQuery.isLoading,
    markApplied: telemetry.markApplied,
    refetch: () => legacyQuery.refetch(),
    transport: 'legacy',
    delivery,
  }
}
